import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Heart, Instagram } from "lucide-react";
import { WA_MAIN, INSTAGRAM_URL } from "@/lib/api";

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className || ""}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const logoY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} id="topo" className="relative grain overflow-hidden bg-cream pt-16" data-testid="hero-section">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brand/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[380px] h-[380px] rounded-full bg-amberbrand/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center relative">
        <div className="relative z-10">
          <motion.div
            style={{ y: logoY }}
            initial={{ opacity: 0, y: 32, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="w-44 sm:w-56 mb-8"
          >
            <motion.img
            src="/logo.png"
            alt="Cestou Recordações"
            className="w-full h-auto animate-float-soft drop-shadow-[0_18px_30px_rgba(74,53,37,0.18)]"
            data-testid="hero-logo"
          />
          </motion.div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-espresso" data-testid="hero-title">
            <MaskedLine delay={0.25}>Cestas e presentes</MaskedLine>
            <MaskedLine delay={0.4} className="italic text-brand">para momentos</MaskedLine>
            <MaskedLine delay={0.55}>especiais!</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-6 text-base sm:text-lg text-wood/70 max-w-md leading-relaxed"
          >
            Cestas de café da manhã, caixas presente, balões e mimos feitos à mão com carinho.
            Escolha no catálogo e peça direto no WhatsApp.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#catalogo"
              data-testid="hero-cta-catalog"
              className="inline-flex items-center gap-2 bg-espresso text-cream px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-wood transition-colors shadow-lg shadow-espresso/20"
            >
              Ver catálogo
              <ArrowDown className="w-4 h-4" />
            </a>
            <a
              href={WA_MAIN}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 border-2 border-[#25D366] text-[#128C7E] px-7 py-3 rounded-full font-semibold text-sm hover:bg-[#25D366] hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Pedir no WhatsApp
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-instagram-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-wood/60 hover:text-brand transition-colors"
            >
              <Instagram className="w-4 h-4" />
              Siga a gente no Instagram: <span className="text-brand">@cestourecordacoes</span>
            </a>
          </motion.div>
        </div>

        <div className="relative">
          <motion.div
            style={{ y: photoY }}
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-md lg:max-w-none"
          >
            <div className="rounded-t-[999px] rounded-b-[2rem] overflow-hidden border-[10px] border-white shadow-[0_32px_80px_rgba(74,53,37,0.25)]">
              <img
                src="/hero.jpg"
                alt="Cesta de café da manhã Cestou Recordações"
                className="w-full h-[420px] sm:h-[520px] lg:h-[580px] object-cover"
                data-testid="hero-photo"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 24, rotate: 6 }}
              animate={{ opacity: 1, x: 0, rotate: 3 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="absolute -bottom-6 -left-4 sm:-left-10 bg-white rounded-2xl shadow-xl shadow-wood/20 px-5 py-4 flex items-center gap-3 border border-wood/10"
            >
              <span className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-brand" />
              </span>
              <div>
                <p className="font-script text-2xl leading-none text-brand">Feito à mão</p>
                <p className="text-xs text-wood/60 mt-0.5">com carinho, para você</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
